import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Sparkles, AlertTriangle, RotateCcw } from 'lucide-react';
import { CompanyRow } from './ThesisCompanyRow';
import { AngleBadge, ANGLE_STYLE, fundNoteFor } from './FundScreenerCard';
import { filterSummary } from './DealFilterPanel';

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

// Polls a fund/deal/angle screening run until it finishes; `initial` = {run, rows} already loaded by the parent
export function useScreenRun(initial) {
  const [run, setRun] = useState(initial?.run || null);
  const [rows, setRows] = useState(initial?.rows || []);
  const pollRef = useRef(null);
  const stop = () => { clearInterval(pollRef.current); pollRef.current = null; };
  const poll = useCallback((runId) => {
    stop();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.infraRun(runId);
        setRun(r.data.run); setRows(r.data.rows || []);
        if (r.data.run?.status !== 'running') {
          stop();
          if (r.data.run?.status === 'done') toast.success(`${(r.data.rows || []).length} Vorschläge gefunden`);
          else if (r.data.run?.status === 'failed') toast.error(r.data.run.error || 'Screening fehlgeschlagen');
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, []);
  useEffect(() => {
    if (initial?.run) { setRun(initial.run); setRows(initial.rows || []); if (initial.run.status === 'running') poll(initial.run.id); }
    // eslint-disable-next-line
  }, [initial?.run?.id, initial?.run?.status]);
  useEffect(() => stop, []);
  const start = async (promise) => {
    try {
      const r = await promise;
      setRun(r.data); setRows([]);
      poll(r.data.id);
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || 'Screening fehlgeschlagen'); return null; }
  };
  // "Screening zurücksetzen": forgets which companies were already screened for this fund/deal/angle set
  const reset = async () => {
    if (!run) return;
    try {
      const r = await api.infraRunReset(run.id);
      toast.success(r.data.message);
      stop(); setRun(null); setRows([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Zurücksetzen fehlgeschlagen'); }
  };
  return { run, rows, start, reset };
}

const DEFAULT_STAGES = ['succession_possible', 'ai_reviewed'];
// The run's filter summary vs. the panel's current filters (backend adds the default stages when none are set).
export function filtersChanged(run, current, currentThemes) {
  if (!run || !current) return false;
  const cur = { ...current };
  if (!(cur.pipeline_stage || []).length) cur.pipeline_stage = DEFAULT_STAGES;
  const a = filterSummary(cur).slice().sort().join(' | ');
  const b = filterSummary(run.filters || {}).slice().sort().join(' | ');
  if (a !== b) return true;
  if (currentThemes === undefined) return false;
  const ranThemes = run.themes_selected ? (run.themes || []).map(t => t.id).sort().join('|') : '';
  return ranThemes !== (currentThemes || []).slice().sort().join('|');
}

// Theme chips above the result list — counts per theme, click = show only that theme's hits.
function ThemeChips({ run, rows, active, onToggle, prefix }) {
  const counts = {};
  rows.forEach(r => { if (r.fund_theme_id) counts[r.fund_theme_id] = (counts[r.fund_theme_id] || 0) + 1; });
  const themes = (run.themes || []).filter(t => counts[t.id] || run.themes_selected);
  if (!themes.length) return null;
  const noTheme = rows.filter(r => !r.fund_theme_id).length;
  return (
    <div className="flex flex-wrap items-center gap-1 w-full" data-testid={`${prefix}-theme-chips`}>
      <span className="text-[10px] text-muted-foreground/70 mr-1">{run.themes_selected ? 'Gesucht nach diesen Themen:' : 'Treffer nach Thema:'}</span>
      {themes.map(t => (
        <button key={t.id} onClick={() => onToggle(t.id)} data-testid={`${prefix}-theme-chip-${t.id}`} aria-pressed={active === t.id}
          className={`text-[10px] px-1.5 py-0.5 rounded-sm border transition-colors ${active === t.id ? 'text-amber-200 border-amber-400/70 bg-amber-500/25' : 'text-muted-foreground border-border hover:text-foreground hover:border-amber-500/40'}`}>
          {t.label} <span className="font-mono opacity-80">{counts[t.id] || 0}</span>
        </button>
      ))}
      {noTheme > 0 && (
        <button onClick={() => onToggle('__none')} data-testid={`${prefix}-theme-chip-none`} aria-pressed={active === '__none'}
          className={`text-[10px] px-1.5 py-0.5 rounded-sm border transition-colors ${active === '__none' ? 'text-amber-200 border-amber-400/70 bg-amber-500/25' : 'text-muted-foreground/70 border-border/60 hover:text-foreground'}`}>
          ohne Thema <span className="font-mono opacity-80">{noTheme}</span>
        </button>
      )}
      {active && <button onClick={() => onToggle(active)} data-testid={`${prefix}-theme-chip-clear`} className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1">alle zeigen</button>}
    </div>
  );
}

// "Lauf 2 · 600 von 6.111 Firmen gescreent" — the screening memory: every run takes the next 300 unseen companies.
function ScreenProgress({ run, prefix, onReset }) {
  const [confirm, setConfirm] = useState(false);
  if (run.screened_total == null || !run.pool_size) return null;
  const pct = Math.min(100, Math.round(run.screened_total / run.pool_size * 100));
  return (
    <div className="w-full space-y-1" data-testid={`${prefix}-screen-progress`}>
      <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
        <span className="font-mono text-foreground/80" data-testid={`${prefix}-run-no`}>Lauf {run.run_no || 1}</span>
        <span>·</span>
        <span data-testid={`${prefix}-screened-total`}>{run.screened_total.toLocaleString('de-DE')} von {run.pool_size.toLocaleString('de-DE')} Firmen im Pool gescreent ({pct} %)</span>
        {run.total_candidates > 0 && <span className="text-muted-foreground/70">· dieser Lauf: {run.total_candidates} neue Firmen</span>}
        {onReset && (confirm ? (
          <span className="inline-flex items-center gap-1 ml-auto" data-testid={`${prefix}-reset-confirm`}>
            Gedächtnis + alle Treffer löschen?
            <button onClick={() => { setConfirm(false); onReset(); }} data-testid={`${prefix}-reset-yes`} className="px-1.5 h-5 rounded-sm bg-red-600 hover:bg-red-500 text-white">Ja</button>
            <button onClick={() => setConfirm(false)} data-testid={`${prefix}-reset-no`} className="px-1.5 h-5 rounded-sm border border-border">Nein</button>
          </span>
        ) : (
          <button onClick={() => setConfirm(true)} data-testid={`${prefix}-reset-btn`} title="Vergisst, welche Firmen für diesen Fonds schon gescreent wurden — der nächste Lauf startet wieder bei Rang 1"
            className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-red-400">
            <RotateCcw className="w-3 h-3" /> Screening zurücksetzen
          </button>
        ))}
      </div>
      <div className="h-1 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-amber-500/80 transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function ScreenResults({ run, rows, label, onRescreen, onReset, rowProps, decorate, prefix, fundName, emptyText, currentFilters, currentThemes }) {
  const [themeActive, setThemeActive] = useState('');
  useEffect(() => { setThemeActive(''); }, [run?.id]);
  if (!run) return null;
  if (run.status === 'running') {
    return (
      <div className="flex items-center gap-2 text-[11px] text-amber-300/90 px-4 py-3 bg-card border border-border rounded-sm" data-testid={`${prefix}-progress`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {run.phase === 'matching' ? 'Datenbank wird nach passenden Firmen durchsucht…' : `Kandidaten werden bewertet… ${run.scored || 0}/${run.total_candidates || '…'}`}
        {(run.run_no || 1) > 1 && run.phase !== 'matching' && <span className="text-muted-foreground/70">· Lauf {run.run_no}, bereits gescreente Firmen werden übersprungen</span>}
      </div>
    );
  }
  if (run.status === 'failed') {
    return <p className="text-[11px] text-red-400 px-4 py-3 bg-card border border-border rounded-sm" data-testid={`${prefix}-error`}>{run.error || 'Screening fehlgeschlagen'}</p>;
  }
  const all = decorate ? decorate(rows) : rows;
  const list = !themeActive ? all : all.filter(r => (themeActive === '__none' ? !r.fund_theme_id : r.fund_theme_id === themeActive));
  const toggleTheme = (id) => setThemeActive(v => (v === id ? '' : id));
  const multiRun = (run.runs_total || 1) > 1;
  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`${prefix}-results`}>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">
          <span className="text-foreground font-medium" data-testid={`${prefix}-results-count`}>{list.length}{themeActive ? ` von ${all.length}` : ''} Vorschläge</span> {label}
          {multiRun && <span className="text-amber-300/80" data-testid={`${prefix}-runs-total`}> · aus {run.runs_total} Läufen</span>}
          {run.hits_hidden > 0 && (
            <span className="text-muted-foreground/70" data-testid={`${prefix}-hits-hidden`}
              title="Treffer früherer Läufe, die zu den Filtern dieses Laufs nicht mehr passen oder inzwischen passed/kontaktiert/gesehen sind, werden ausgeblendet">
              {' '}· {run.hits_hidden} ausgeblendet (passen nicht mehr zu Filtern oder Deal-Stand)
            </span>
          )}
          {!multiRun && run.total_hits > all.length && <span> (Top {all.length} von {run.total_hits} Treffern)</span>}
          {run.by_angle && <span className="text-muted-foreground/70">{' · '}{Object.entries(run.by_angle).map(([k, v]) => `${v}× ${ANGLE_STYLE[k]?.label || k}`).join(' · ')}</span>}
          {run.pool_size != null && <span className="text-muted-foreground/60"> · {run.pool_size.toLocaleString()} Firmen im Pool</span>}
          {run.finished_at && <span className="text-muted-foreground/50"> · {fmtTs(run.finished_at)}</span>}
        </p>
        <ScreenProgress run={run} prefix={prefix} onReset={onReset} />
        {run.exhausted && (
          <div className="w-full flex items-center gap-2 flex-wrap text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-1.5" data-testid={`${prefix}-exhausted`}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 min-w-[200px]">
              Pool erschöpft — alle passenden Firmen wurden in früheren Läufen bereits gescreent{run.remaining > 0 ? ` (${run.remaining.toLocaleString('de-DE')} übrige Firmen passen zu keinem Suchbegriff)` : ''}.
              {onReset ? ' „Screening zurücksetzen" beginnt wieder bei Rang 1; andere Filter öffnen weitere Firmen.' : ' Andere Filter öffnen weitere Firmen.'}
            </span>
          </div>
        )}
        <ThemeChips run={run} rows={all} active={themeActive} onToggle={toggleTheme} prefix={prefix} />
        {run.filters && Object.keys(run.filters).length > 0 && (
          <div className="flex flex-wrap gap-1 w-full" data-testid={`${prefix}-run-filters`}>
            <span className="text-[10px] text-muted-foreground/70 mr-1">Filter dieses Laufs:</span>
            {filterSummary(run.filters).map(c => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{c}</span>)}
          </div>
        )}
        {run.status === 'done' && filtersChanged(run, currentFilters, currentThemes) && (
          <div className="w-full flex items-center gap-2 flex-wrap text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-1.5" data-testid={`${prefix}-filters-stale`}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 min-w-[200px]">Filter oder Themen oben wurden seit diesem Lauf geändert — diese Ergebnisse gelten noch für die alte Auswahl.</span>
            {onRescreen && (
              <button onClick={onRescreen} data-testid={`${prefix}-rescreen-stale-btn`}
                className="inline-flex items-center gap-1 border border-amber-500/50 rounded-sm px-2 py-0.5 hover:bg-amber-500/20">
                <Sparkles className="w-3 h-3" /> Mit aktuellen Filtern erneut screenen
              </button>
            )}
          </div>
        )}
        {onRescreen && (
          <button onClick={onRescreen} data-testid={`${prefix}-rescreen-btn`} className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
            title={run.exhausted ? 'Pool erschöpft — erst zurücksetzen oder Filter ändern' : 'Bewertet die nächsten noch nicht gescreenten Firmen (bis zu 300)'}>
            <Sparkles className="w-3 h-3" /> {run.exhausted ? 'Erneut screenen' : 'Nächste 300 screenen'}
          </button>
        )}
      </div>
      {list.length === 0 && <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid={`${prefix}-empty`}>{emptyText || 'Keine passende Firma im Bestand gefunden.'}</p>}
      {list.map((r, i) => (
        <CompanyRow key={r.id} r={r} i={i} score={r.fund_score} reason={r.fund_reason} prefix={prefix}
          badge={<><AngleBadge angle={r.fund_angle} label={r.fund_angle_label} link={r.fund_portfolio_link} />
            {r.fund_asset_heavy != null && <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-orange-500/40 text-orange-300 bg-orange-500/10 ml-1" title="KI-Einschätzung: wie anlagenintensiv / asset-heavy das Geschäftsmodell ist (0–100)" data-testid={`${prefix}-row-assetheavy-${i}`}>Asset-Heavy {r.fund_asset_heavy}</span>}
            {r.fund_theme && <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-border/70 text-muted-foreground ml-1" title="Thema aus dem Fonds-Profil" data-testid={`${prefix}-row-theme-${i}`}>{r.fund_theme}</span>}
            {multiRun && r.fund_run_no && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-amber-500/30 text-amber-300/80 ml-1" title="Aus welchem Screening-Lauf dieser Treffer stammt" data-testid={`${prefix}-row-run-${i}`}>Lauf {r.fund_run_no}</span>}</>}
          {...rowProps} onOpen={(id) => rowProps.onOpen(id, { fundNote: fundNoteFor(fundName || run.fund_name, r) })} />
      ))}
    </div>
  );
}
